import Base from './Base';
import Button, { ButtonThemes } from '../../button/Button';
import { Icon } from '../../icon/Icon';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { Zoom, Thumbs, Navigation, Mousewheel } from 'swiper/modules';
import { CustomType } from '../Image.types';
import { IconTheme } from '../../icon/Icon.types';
import { getClassNames } from '../../../helpers/classHelpers';
import { useCallback, useRef, useState } from 'react';
import { Icons } from '../../icon/icons/material';
import { openExternalLink } from '../../../helpers/routeHelpers';
import styles from '../Image.module.scss';

import 'swiper/css';
import 'swiper/css/zoom';
import 'swiper/css/mousewheel';
import "swiper/css/pagination";
import "swiper/css/navigation";

const MAX_ZOOM = 5;
const MIN_ZOOM = 1;

export default function ImageCustom({ className, enableThumbs, enableMousewheel, enableZoom, images, onClick, onExpand }: CustomType) {
    const ref = useRef<SwiperRef>(null);
    const [zoom, setZoom] = useState(MIN_ZOOM);

    // React on zoom scale change
    const updateSwiperZoom = useCallback((zoom: number) => {
        if (!ref.current) return;
        ref.current.swiper.zoom.in(zoom);
    }, []);

    const handleManualZoom = useCallback((s: number) => setZoom(s), []);

    const zoomIn = useCallback(() => setZoom(zoom => {
        const $zoom = zoom < MAX_ZOOM ? zoom + 1 : zoom;
        updateSwiperZoom($zoom);
        return $zoom;
    }), [updateSwiperZoom]);

    const zoomOut = useCallback(() => setZoom(zoom => {
        const $zoom = zoom > MIN_ZOOM ? zoom - 1 : zoom;
        updateSwiperZoom($zoom);
        return $zoom;
    }), [updateSwiperZoom]);

    const zoomOutAll = useCallback(() => {
        setZoom(MIN_ZOOM);
        updateSwiperZoom(MIN_ZOOM);
    }, [updateSwiperZoom]);

    const downloadImage = useCallback(async () => {
        if (!ref.current) return;

        const currentSlide = ref.current.swiper.slides[ref.current.swiper.activeIndex];
        const image = currentSlide.querySelector('img');
        if (!image) return;

        const imageSource = image.currentSrc.split('?')[0];
        if (!imageSource) return;


        // Open in new window
        const newWindow = openExternalLink(imageSource);
        if (!newWindow) {
            console.error('Failed to open new window');
            return;
        }

        // Download image

        // try {
        // const response = await fetch((imageSource) as any, { mode: 'cors' });
        // if (!response.ok) throw new Error('Network response was not ok');

        // const blob = await response.blob();
        // const blobUrl = URL.createObjectURL(blob);

        // const a = document.createElement('a');
        // a.href = blobUrl;
        // a.download = image.src.split('/').pop() || 'image';
        // document.body.appendChild(a);
        // a.click();
        // a.remove();

        // URL.revokeObjectURL(blobUrl);
        // } catch (err) {
        //     console.error('Image download failed:', err);
        // }
    }, []);

    const modules = [];
    const zoomMods = [Zoom];
    const thumbsMods = [Thumbs, Navigation, Mousewheel];
    if (enableZoom) modules.push(...zoomMods);
    if (enableThumbs) modules.push(...thumbsMods);

    const renderSwiper = <div className={getClassNames([styles.slider, className])}>
        <Swiper
            ref={ref}
            onZoomChange={(_: any, s: number) => handleManualZoom(s)}
            tabIndex={-1}
            mousewheel={{
                enabled: enableMousewheel
            }}
            zoom={{
                maxRatio: MAX_ZOOM,
                minRatio: MIN_ZOOM,
            }}
            modules={modules}
            className={getClassNames(['swiper-thumbs', '_center-flex'])}
            navigation={{ prevEl: '.swiper-thumb-prev', nextEl: '.swiper-thumb-next', }}
        >
            {images.map((img, i) => <SwiperSlide key={i} >
                <div className="swiper-zoom-container">
                    <Base
                        className={getClassNames([img.className, styles['custom-image']])}
                        {...img}
                        showLoadingWhenFetching
                        onClick={onClick}
                        onExpand={onExpand}
                    />
                </div>
            </SwiperSlide>)}
        </Swiper>

        {enableThumbs && images.length > 1 && <>
            <Button label={<Icon.Base theme={IconTheme.inherit} iconName={Icons.chevronRight} title={''} /> as any} className="swiper-thumb-prev swiper-thumb-navigation" theme={ButtonThemes.textSecondary} />
            <Button label={<Icon.Base theme={IconTheme.inherit} iconName={Icons.chevronRight} title={''} /> as any} className="swiper-thumb-next swiper-thumb-navigation" theme={ButtonThemes.textSecondary} />
        </>}

        {enableZoom && <div className={styles.actions}>
            <Icon.Base isDisabled={zoom === MAX_ZOOM} onClick={zoomIn} theme={IconTheme.inherit} iconName={Icons.zoomIn} title='' />
            <Icon.Base isDisabled={zoom === MIN_ZOOM} onClick={zoomOut} theme={IconTheme.inherit} iconName={Icons.zoomOut} title='' />
            <Icon.Base isDisabled={zoom === MIN_ZOOM} onClick={zoomOutAll} theme={IconTheme.inherit} iconName={Icons.zoomOutAll} title='' />
            <Icon.Adaptive onAsyncClick={downloadImage} theme={IconTheme.inherit} iconName={Icons.openInNewWindow} title='' />
        </div>}
    </div>;

    return renderSwiper;
};